import { captureException } from '@sentry/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { LDProvider } from 'launchdarkly-react-client-sdk';
import type React from 'react';
import { useAuth } from 'react-oidc-context';

import { ldFeatureFlags } from '@/config/features';
import { ProgressProvider } from '@/context/progress';
import { ThemeProvider } from '@/context/theme';
import { FirebaseProvider } from '@/lib/firebase/components/firebase-provider';
import { queryClient } from '@/lib/query-client';

const LD_TIMEOUT_S = 2; // 2 seconds

const OidcFirebaseProvider = ({ children }: React.PropsWithChildren) => {
  const auth = useAuth();

  return (
    <FirebaseProvider isAuthenticated={auth.isAuthenticated} isLoadingAuthenticated={auth.isLoading} onSettingsError={captureException}>
      {children}
    </FirebaseProvider>
  );
};
OidcFirebaseProvider.displayName = 'OidcFirebaseProvider';

const Providers = ({ children }: React.PropsWithChildren) => {
  return (
    <LDProvider clientSideID={import.meta.env.VITE_APP_LD_KEY} flags={ldFeatureFlags} timeout={LD_TIMEOUT_S}>
      <QueryClientProvider client={queryClient}>
        <OidcFirebaseProvider>
          <ThemeProvider>
            <ProgressProvider>{children}</ProgressProvider>
          </ThemeProvider>
        </OidcFirebaseProvider>
      </QueryClientProvider>
    </LDProvider>
  );
};
Providers.displayName = 'Providers';

export default Providers;
